import Api from 'entities/helpers/api';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import DeleteModal from 'apps/search/components/delete-modal';
import AllUsersStore from 'apps/users/stores/all-users-store';
import SegmentSource from 'apps/filters/sources/segment-source';
import { FlexTable, useFlexTableApi } from '@evertrue/sdk';
import { useIdentity } from 'base/identity-resolver';
import { useFluxStore } from '@evertrue/et-flux';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, LinkButton, Icon, Avatar, IconButton, ButtonTooltip, useOpenClose } from '@evertrue/et-components';

const OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Biweekly', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Never', value: 'never' },
];

const FREQUENCY_LABELS = Object.fromEntries(OPTIONS.map(({ label, value }) => [value, label]));

// Notification-frequency columns that are click-to-edit on the saved-search table.
const EDITABLE_FREQUENCY_FIELDS = ['email_frequency', 'push_frequency'];

/**
 * Renders an editable frequency value so it reads as an interactive dropdown
 * control (value + caret) rather than plain text.
 */
const renderFrequencyCell = (value) => (
  <div className="frequency-cell" title="Click to change notification frequency">
    <span>{FREQUENCY_LABELS[value] || 'Never'}</span>
    <Icon icon="chevron-down" className="frequency-cell--caret" />
  </div>
);

/**
 * Edit control for the notification-frequency cells. Commits the change the
 * moment an option is chosen (rather than waiting for the cell to lose focus)
 * so a single selection immediately fires the save.
 */
const FrequencyEditCell = ({ id, field, value, apiRef }) => {
  const handleChange = async (event) => {
    const newValue = event.target.value;
    await apiRef.current?.setEditCellValue({ id, field, value: newValue });
    apiRef.current?.stopCellEditMode({ id, field });
  };

  return (
    <select className="frequency-edit-cell" value={value} onChange={handleChange} autoFocus>
      {OPTIONS.map(({ label, value: optionValue }) => (
        <option key={optionValue} value={optionValue}>
          {label}
        </option>
      ))}
    </select>
  );
};

FrequencyEditCell.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  field: PropTypes.string,
  value: PropTypes.string,
  apiRef: PropTypes.object,
};

const userStoreMap = () => {
  return {
    users: AllUsersStore.getAllGivingTreeUsers(),
  };
};

const ListIndexTable = ({
  listType = 'static',
  tableKey,
  lists = [],
  total = 0,
  loading = false,
  sortModel = [
    {
      field: 'name',
      sort: 'asc',
    },
  ],
  setSortModel = () => {},
  paginationModel = {
    page: 0,
    pageSize: 25,
  },
  setPaginationModel = () => {},
  rowSelectionModel = {
    type: 'include',
    ids: new Set(),
  },
  updateRowSelectionModel = () => {},
  deleteList = () => {},
  deleteLists = () => {},
  onRowUpdate = () => {},
  search = '',
  onSearchChange = () => {},
  serverSidePagination = true, // if our API supports pagination, use that. otherwise, use client-side pagination
  emptyStateMessage = 'No Lists found',
  emptyStateIcon = 'list',
}) => {
  const { user } = useIdentity();
  const { users } = useFluxStore(AllUsersStore, userStoreMap);
  const apiRef = useFlexTableApi();
  const [preferences, setPreferences] = useState([]);
  const [listToDelete, setListToDelete] = useState(null);
  const [isDeleteModalOpen, openModal, closeModal] = useOpenClose();
  const searchDebounceRef = useRef(null);

  const handleFilterModelChange = (model) => {
    const query = (model.quickFilterValues || []).join(' ');
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      onSearchChange(query);
    }, 400);
  };

  useEffect(() => {
    return () => clearTimeout(searchDebounceRef.current);
  }, []);

  // The EMS preferences endpoint returns a bare array of preference records;
  // tolerate a `{ preferences: [...] }` envelope too in case the shape changes.
  const loadPreferences = useCallback(async () => {
    try {
      const data = await Api.EMS.PREFS.get();
      const prefs = Array.isArray(data) ? data : data?.preferences || [];
      setPreferences(prefs);
    } catch {
      // EMS service may be unavailable (e.g. CORS in local dev)
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Persist the frequency change, then refetch so the displayed value reflects
  // what actually saved rather than a stale, optimistic row.
  const handleRowUpdate = async (newRow, oldRow) => {
    const result = await onRowUpdate(newRow, oldRow);
    await loadPreferences();
    return result;
  };

  // Open the frequency dropdown on a single click so the cell behaves like an
  // obvious select control instead of requiring a hidden double-click.
  const handleCellClick = (params) => {
    if (!EDITABLE_FREQUENCY_FIELDS.includes(params.field) || params.cellMode === 'edit') return;
    apiRef.current?.startCellEditMode({ id: params.id, field: params.field });
  };

  const openDeleteModal = (listId) => {
    setListToDelete(listId);
    openModal();
  };

  const handleDelete = async (listId, setSubmitting) => {
    if (!listId) return;
    try {
      setSubmitting(true);
      await deleteList(listId);
    } finally {
      closeModal();
      updateRowSelectionModel(
        {
          type: 'include',
          ids: new Set(),
        },
        user.id
      );
      setSubmitting(false);
    }
  };

  const getFullLists = () => {
    if (listType === 'static') {
      return lists;
    } else {
      return lists.map((list) => {
        const preference = preferences.find((p) => p.key === `list:${list.id}:`);
        // Guard against a malformed EMS record (enabled but no frequency, per SIG-853's
        // AMS-upgrade data-shape issue) crashing the whole table render.
        const emailFrequency =
          preference?.email_enabled && preference.email_frequency ? preference.email_frequency.toLowerCase() : 'never';
        const pushFrequency =
          preference?.push_enabled && preference.push_frequency ? preference.push_frequency.toLowerCase() : 'never';
        return {
          ...list,
          email_frequency: emailFrequency,
          push_frequency: pushFrequency,
        };
      });
    }
  };

  const columns =
    listType === 'static'
      ? [
          {
            field: 'name',
            headerName: 'Name',
            type: 'string',
            flex: 1,
            sortable: true,
            renderCell: ({ row }) => {
              const data = row.rawData || row;
              return (
                <div key={`list-name-${data.id}`}>
                  {data.private ? (
                    <Icon icon="lock" className="lists-page--icon" />
                  ) : (
                    <Icon icon="group" className="lists-page--icon" />
                  )}
                  <Link title={data.name} href={`list/${data.id}`}>
                    {data.name}
                  </Link>
                </div>
              );
            },
          },
          {
            field: 'user_id',
            headerName: 'Owner',
            flex: 1,
            type: 'string',
            sortable: false,
            valueGetter: (value) => {
              const user = users[value];
              return user ? user.name : 'Unknown';
            },
            renderCell: ({ row }) => {
              const data = row.rawData || row;
              const user = users[data?.user_id];
              return (
                <div key={`avatar-${data.id}`} className="lists-page--avatar">
                  <Avatar name={user?.name ?? "User's avatar"} width={30} url={user && user.avatar} />
                  <div>{user && user.name}</div>
                </div>
              );
            },
          },
          {
            field: 'updated_at',
            headerName: 'Last Updated',
            flex: 1,
            type: 'date',
            filterable: false,
            getApplyQuickFilterFn: () => null, // Disable quick filter (search) for date fields
            sortable: true,
            valueGetter: (value) => {
              if (!value) {
                return value;
              }
              return value * 100;
            },
            valueFormatter: (value) => {
              if (value == null) {
                return '';
              }
              return EverTrue.moment(value / 100).format('MMMM Do, YYYY');
            },
          },
          {
            field: 'actions',
            type: 'actions',
            flex: 0.3,
            hideable: false,
            filterable: false,
            sortable: false,
            renderCell: ({ row }) => {
              const data = row.rawData || row;
              if (data.user_id === user.id)
                return (
                  <IconButton
                    key={`delete-${data.id}`}
                    title={`Delete List ${data.name}`}
                    icon="trash"
                    onClick={() => openDeleteModal(data.id)}
                    aria-label="Delete List"
                  />
                );
              else
                return (
                  <ButtonTooltip
                    key={`delete-disabled-${data.id}`}
                    children="Delete option is disabled because you are not the owner of this list"
                    contentPadding={'4px 8px'}
                    trigger={<Icon icon="trash" style={{ color: 'grey' }} />}
                  />
                );
            },
          },
        ]
      : [
          {
            field: 'name',
            headerName: 'Name',
            type: 'string',
            flex: 1,
            editable: false,
            sortable: true,
            renderCell: ({ row }) => {
              const data = row.rawData || row;
              return (
                <div key={`list-name-${data.id}`}>
                  {data.private ? (
                    <Icon icon="lock" className="lists-page--icon" />
                  ) : (
                    <Icon icon="group" className="lists-page--icon" />
                  )}
                  <LinkButton title="View Segment" onClick={() => SegmentSource.applySegment(data.id)}>
                    {data.name}
                  </LinkButton>
                </div>
              );
            },
          },
          {
            field: 'email_frequency',
            headerName: 'Email Frequency',
            flex: 1,
            type: 'singleSelect',
            valueOptions: OPTIONS,
            editable: true,
            sortable: false,
            description: 'Click a cell to change how often you receive email notifications for this saved search',
            renderCell: ({ value }) => renderFrequencyCell(value),
            renderEditCell: ({ id, field, value }) => (
              <FrequencyEditCell id={id} field={field} value={value} apiRef={apiRef} />
            ),
            renderHeader: () => (
              <div key="email_frequency">
                <Icon icon="email" style={{ marginRight: 10, fontSize: 20 }} />
                Email Updates
              </div>
            ),
          },
          {
            field: 'push_frequency',
            headerName: 'Push Frequency',
            flex: 1,
            type: 'singleSelect',
            valueOptions: OPTIONS,
            editable: true,
            sortable: false,
            description: 'Click a cell to change how often you receive push notifications for this saved search',
            renderCell: ({ value }) => renderFrequencyCell(value),
            renderEditCell: ({ id, field, value }) => (
              <FrequencyEditCell id={id} field={field} value={value} apiRef={apiRef} />
            ),
            renderHeader: () => (
              <div key="push_frequency">
                <Icon icon="phone-android" style={{ marginRight: 10, fontSize: 20 }} />
                Mobile Notifications
              </div>
            ),
          },
          {
            field: 'actions',
            type: 'actions',
            flex: 0.3,
            hideable: false,
            filterable: false,
            sortable: false,
            renderCell: ({ row }) => {
              const data = row.rawData || row;
              if (data.user_id === user.id)
                return (
                  <IconButton
                    title={`Delete Saved Search ${data.name}`}
                    key={`delete-${data.id}`}
                    icon="trash"
                    onClick={() => openDeleteModal(data.id)}
                    aria-label="Delete List"
                  />
                );
              else
                return (
                  <ButtonTooltip
                    key={`delete-disabled-${data.id}`}
                    children="Delete option is disabled because you are not the owner of this list"
                    contentPadding={'4px 8px'}
                    trigger={<Icon icon="trash" style={{ color: 'grey' }} />}
                  />
                );
            },
          },
        ];

  const autoColumns =
    listType === 'static' ? ['name', 'user_id', 'updated_at'] : ['name', 'email_frequency', 'push_frequency'];
  return (
    <>
      <FlexTable
        apiRef={apiRef}
        tableKey={tableKey} // used to identify the table in local storage
        initialState={{
          pagination: {
            paginationModel,
          },
          sorting: {
            sortModel,
          },
          rowSelection: {
            rowSelectionModel,
          },
        }}
        columns={columns}
        rows={getFullLists()}
        // Custom Toolbar Props
        slotProps={{
          columnsManagement: {
            getTogglableColumns: () => autoColumns, // columns that can be toggled on/off in the toolbar
          },
          toolbar: {
            showExport: true, // show export button
            showQuickFilter: true, // show quick filter input ("search bar")
            quickFilterProps: {
              slotProps: {
                root: {
                  placeholder: listType === 'static' ? 'Search by list name...' : 'Search by name...',
                },
              },
            },
          },
        }}
        showToolbar // must be true to show the toolbar
        onCellClick={handleCellClick}
        processRowUpdate={handleRowUpdate}
        // Custom Table View Props
        loading={loading}
        emptyStateMessage={emptyStateMessage}
        emptyStateIcon={emptyStateIcon}
        autosizeOptions={{
          columns: autoColumns,
          includeOutliers: true,
          includeHeaders: true,
        }}
        // Pagination Props
        {...(serverSidePagination
          ? {
              rowCount: total,
              paginationMode: 'server',
              paginationModel: paginationModel,
              onPaginationModelChange: setPaginationModel,
            }
          : {
              paginationMode: 'client',
            })}
        pageSizeOptions={[10, 25, 50, 100]}
        // Filter / Search Props
        filterMode="server"
        onFilterModelChange={handleFilterModelChange}
        // Sorting Props
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        // Row Selection Props
        checkboxSelection
        rowSelection
        isRowSelectable={(params) => {
          // can customize row selection logic here
          return true;
        }}
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newModel) => {
          updateRowSelectionModel(newModel, user.id); // this is its own callback because we do stuff with the user ID
        }}
        emptyStateIcon={<Icon icon={emptyStateIcon} size={5} />}
        disableColumnMenu={false}
        disableColumnSelector={listType === 'dynamic'} // disable column selector for dynamic lists
      />
      {/* Delete List Modal */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeModal}
        handleDelete={handleDelete}
        title="Confirm Delete"
        body={`Are you sure you want to delete this ${listType === 'static' ? 'list' : 'saved search'}?`}
        list_id={listToDelete}
      />
    </>
  );
};

ListIndexTable.propTypes = {
  listType: PropTypes.string, // 'static' or 'dynamic'
  tableKey: PropTypes.string.isRequired,
  lists: PropTypes.array,
  total: PropTypes.number,
  loading: PropTypes.bool,
  deleteList: PropTypes.func,
  deleteLists: PropTypes.func,
  onRowUpdate: PropTypes.func,
  sortModel: PropTypes.array,
  setSortModel: PropTypes.func,
  paginationModel: PropTypes.object,
  setPaginationModel: PropTypes.func,
  rowSelectionModel: PropTypes.object,
  updateRowSelectionModel: PropTypes.func,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  serverSidePagination: PropTypes.bool,
  emptyStateMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  emptyStateIcon: PropTypes.string,
};

export default ListIndexTable;
